import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { maxLengthValidator } from '@common/helpers';
import { SpecialAssistanceRequestState } from '@common/helpers/constants/my-booking/passenger-information-input-modal.state';
import { PregnantMasterData } from '@common/interfaces/mybooking/modals/passenger-information-input/passenger-information.interface';
import { SpecialAssistanceRequestPregnantData } from '@common/interfaces/mybooking/modals/passenger-information-input/special-assistance-request-modal.interface';
import { SelectComponent } from '@lib/components/base-ui-components/form/select/select.component';
import { AswValidators } from '@lib/helpers/validate/validators';
import { ValidatePhoneNumberOptions, ValidationErrorInfo } from '@lib/interfaces/validate';
import { CommonLibService } from '@lib/services';

/**
 * サポート情報入力モーダル
 */
@Component({
  selector: 'asw-special-assistance-request-modal-pregnant',
  templateUrl: './special-assistance-request-modal-pregnant.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SpecialAssistanceRequestModalPregnantComponent implements OnInit, OnDestroy {
  @Input()
  get data() {
    return this._data;
  }
  set data(value: SpecialAssistanceRequestPregnantData | undefined) {
    this._data = value;
  }
  private _data?: SpecialAssistanceRequestPregnantData;

  @Output()
  dataChange: EventEmitter<SpecialAssistanceRequestPregnantData> =
    new EventEmitter<SpecialAssistanceRequestPregnantData>();

  @Input()
  get master() {
    return this._master;
  }
  set master(value: PregnantMasterData | undefined) {
    this._master = value;
  }
  private _master?: PregnantMasterData;

  public id = this.constructor.name;
  public isInvalidDate: boolean = false;
  public countryPhoneExtension = '';
  public dateFrom = new Date();
  public dateTo = new Date(this.dateFrom.getFullYear() + 1, this.dateFrom.getMonth(), this.dateFrom.getDate());

  public dueDateError: string | ValidationErrorInfo = '';

  //電話番号バリデータ
  public phoneValidatorOption: ValidatePhoneNumberOptions = {
    telCountryCode: this.countryPhoneExtension,
    params: SpecialAssistanceRequestState.PhoneNumber.params,
  };

  public conditionFc = new FormControl('', [AswValidators.required(SpecialAssistanceRequestState.Condition)]);

  public expectedDateFc = new FormControl<Date | null>(null, [
    AswValidators.required(SpecialAssistanceRequestState.dueDate),
  ]);

  public doctorNameFc = new FormControl('', [
    AswValidators.required(SpecialAssistanceRequestState.DoctorName),
    AswValidators.alphaNumericSpace(SpecialAssistanceRequestState.DoctorName),
    maxLengthValidator(SpecialAssistanceRequestState.DoctorNameMaxLength),
  ]);

  public phoneCountryCodeFc = new FormControl('', [AswValidators.required(SpecialAssistanceRequestState.PhoneCountry)]);

  public phoneNumberFc = new FormControl('');

  phoneNumberValidatorRefresh() {
    this.phoneValidatorOption.telCountryCode = this.countryPhoneExtension;
    this.phoneNumberFc.setValidators([
      AswValidators.required({ params: this.phoneValidatorOption.params }),
      AswValidators.phoneNumber(this.phoneValidatorOption),
    ]);
    this.phoneNumberFc.updateValueAndValidity();
  }

  constructor(private _change: ChangeDetectorRef, private _common: CommonLibService) {}

  ngOnInit(): void {
    this.refresh();
  }

  ngOnDestroy(): void {
    this.apply();
  }

  refresh() {
    this.doctorNameFc.setValue(this.data?.doctorName ?? '');
    this.countryPhoneExtension = this.data?.doctorCountryPhoneExtension
      ? this.data?.doctorCountryPhoneExtension
      : this._common.aswContextStoreService.aswContextData.posCountryCode === 'JP'
      ? '81'
      : '';

    const country = this.master?.countries?.find(
      (country) => country?.international_tel_country_code === this.countryPhoneExtension
    );
    const countryCode = country?.country_2letter_code ?? '';
    // ※PNR情報取得API応答.data.travelerSupports.＜当該travelers.id>.pregenant.doctor.phone.countryPhoneExtensionが(＋1)の場合には優先的にアメリカ合衆国/カナダ
    const phoneCountryCode = country?.international_tel_country_code === '1' ? 'US' : countryCode;
  
    this.phoneCountryCodeFc.setValue(phoneCountryCode);
    this.phoneNumberFc.setValue(this.data?.doctorPhoneNumber ?? '');
    this.expectedDateFc.setValue(this.data?.dueDate ?? null);
    this.conditionFc.setValue(this.data?.condition ?? '');

    this.phoneNumberValidatorRefresh();

    this._change.markForCheck();
  }

  changeExpectedDate(event: Date | null) {
    this.expectedDateFc.setValue(event);
    this.dueDateError = '';
  }

  apply() {
    this.dueDateError = !this.expectedDateFc.value ? SpecialAssistanceRequestState.dueDate : '';
    this.phoneNumberFc.markAsTouched();
    this.doctorNameFc.markAsTouched();
    this.expectedDateFc.markAllAsTouched();
    this.phoneCountryCodeFc.markAsTouched();
    this.conditionFc.markAsTouched();
    this._change.detectChanges();

    if (!this.isError()) {
      const value: SpecialAssistanceRequestPregnantData = {
        condition: this.conditionFc.value ?? '',
        doctorName: this.doctorNameFc.value ?? '',
        doctorCountryPhoneExtension: this.countryPhoneExtension,
        doctorEncryptedPhoneNumber: '',
        doctorPhoneNumber: this.phoneNumberFc.value ?? '',
        dueDate: this.expectedDateFc.value!,
      };
      this.data = value;
      this.dataChange.emit(value);
    }
  }

  public isError() {
    return Boolean(
      this.doctorNameFc.errors !== null ||
        this.phoneNumberFc.errors !== null ||
        this.phoneCountryCodeFc.errors !== null ||
        this.conditionFc.errors !== null ||
        this.dueDateError
    );
  }

  // (48)	医師電話番号国の値更新処理　※サポート情報入力モーダル(妊婦情報グループ)
  changeCountry(event: SelectComponent) {
    this.countryPhoneExtension =
      this.master?.countries?.find((country) => country?.country_2letter_code === event.data)
        ?.international_tel_country_code ?? '';
    this.phoneNumberValidatorRefresh();
    this._change.detectChanges();
  }
}
